
import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { Users, Calendar, Plus, Minus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";

interface PackagePricing {
  id: string;
  package_id: number;
  base_price: number;
  per_person_price: number;
  per_night_price: number;
  min_nights: number;
  max_nights: number;
  min_persons: number;
  max_persons: number;
}

interface PackageAddon {
  id: string;
  package_id: number;
  name: string;
  description: string;
  price: number;
  addon_type: string;
  is_per_person: boolean;
  is_per_night: boolean;
  is_active: boolean;
  sort_order: number;
}

interface RoomPackage {
  id: string;
  package_id: number;
  room_type: string;
  room_name: string;
  capacity: number;
  base_price: number;
  price_per_extra_person: number;
  max_extra_persons: number;
  amenities: string[];
  is_active: boolean;
}

interface PackagePriceCalculatorProps {
  packageId: number;
  packageName: string;
  onPriceChange: (price: number, details: any) => void;
}

const PackagePriceCalculator = ({ packageId, packageName, onPriceChange }: PackagePriceCalculatorProps) => {
  const [persons, setPersons] = useState(2);
  const [nights, setNights] = useState(3);
  const [selectedRoom, setSelectedRoom] = useState<string>("");
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [extraPersons, setExtraPersons] = useState(0);

  // Fetch pricing data
  const { data: pricing, isLoading: pricingLoading, error: pricingError } = useQuery({
    queryKey: ["package-pricing", packageId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sns_package_pricing")
        .select("*")
        .eq("package_id", packageId)
        .order("created_at", { ascending: false })
        .limit(1);
      
      if (error) {
        throw error;
      }
      
      return data && data.length > 0 ? data[0] as PackagePricing : null;
    },
  });

  // Fetch addons
  const { data: addons = [] } = useQuery({
    queryKey: ["package-addons", packageId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sns_package_addons")
        .select("*")
        .eq("package_id", packageId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as PackageAddon[];
    },
  });

  // Fetch room packages
  const { data: rooms = [] } = useQuery({
    queryKey: ["room-packages", packageId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sns_room_packages")
        .select("*")
        .eq("package_id", packageId)
        .eq("is_active", true);
      if (error) throw error;
      return data as RoomPackage[];
    },
  });

  // Calculate total price
  const calculatePrice = useCallback(() => {
    if (!pricing) return 0;

    let total = pricing.base_price;
    
    // Add per person cost
    total += pricing.per_person_price * persons;
    
    // Add per night cost
    total += pricing.per_night_price * nights;
    
    // Add room upgrade cost
    if (selectedRoom) {
      const room = rooms.find(r => r.id === selectedRoom);
      if (room) {
        total += room.base_price;
        total += room.price_per_extra_person * extraPersons;
      }
    }
    
    // Add addon costs
    selectedAddons.forEach(addonId => {
      const addon = addons.find(a => a.id === addonId);
      if (addon) {
        let addonCost = addon.price;
        if (addon.is_per_person) addonCost *= persons;
        if (addon.is_per_night) addonCost *= nights;
        total += addonCost;
      }
    });

    return total;
  }, [pricing, persons, nights, selectedRoom, selectedAddons, extraPersons, rooms, addons]);

  const totalPrice = calculatePrice();

  useEffect(() => {
    if (!pricing) return;
    
    const selectedRoomData = rooms.find(r => r.id === selectedRoom);
    const selectedAddonsData = addons.filter(a => selectedAddons.includes(a.id));
    
    const priceDetails = {
      persons,
      nights,
      extraPersons,
      selectedRoom: selectedRoomData,
      selectedAddons: selectedAddonsData,
      breakdown: {
        basePrice: pricing.base_price || 0,
        personCost: (pricing.per_person_price || 0) * persons,
        nightCost: (pricing.per_night_price || 0) * nights,
        roomCost: selectedRoomData ? selectedRoomData.base_price + (selectedRoomData.price_per_extra_person * extraPersons) : 0,
        addonsCost: selectedAddonsData.reduce((sum, addon) => {
          let cost = addon.price;
          if (addon.is_per_person) cost *= persons;
          if (addon.is_per_night) cost *= nights;
          return sum + cost;
        }, 0)
      }
    };
    
    // Use setTimeout to prevent potential infinite loops and improve performance
    const timeoutId = setTimeout(() => {
      onPriceChange(totalPrice, priceDetails);
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [totalPrice, persons, nights, selectedRoom, selectedAddons, extraPersons, pricing, rooms, addons, onPriceChange]);

  // Show loading or error states
  if (pricingLoading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-pulse">Loading price calculator...</div>
      </div>
    );
  }

  if (pricingError) {
    console.error('Pricing error:', pricingError);
    return (
      <div className="p-6 text-center text-red-600">
        <div>Error loading pricing data: {pricingError.message}</div>
      </div>
    );
  }

  if (!pricing) {
    return (
      <div className="p-6 text-center text-gray-600">
        <div>No pricing configuration found for this package</div>
      </div>
    );
  }

  const selectedRoomData = rooms.find(r => r.id === selectedRoom);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-shell-dark mb-2">Customize Your {packageName}</h3>
        <p className="text-gray-600 text-sm">Select your preferences to get an accurate price</p>
      </div>

      {/* Basic Details Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-shell-primary" />
            Basic Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Persons */}
            <div>
              <label className="block text-sm font-medium mb-2">Number of Persons</label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPersons(Math.max(pricing.min_persons, persons - 1))}
                  disabled={persons <= pricing.min_persons}
                  className="h-8 w-8"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <div className="flex-1 text-center">
                  <span className="text-lg font-semibold">{persons}</span>
                  <p className="text-xs text-gray-500">{pricing.min_persons}-{pricing.max_persons} allowed</p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPersons(Math.min(pricing.max_persons, persons + 1))}
                  disabled={persons >= pricing.max_persons}
                  className="h-8 w-8"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Nights */}
            <div>
              <label className="block text-sm font-medium mb-2">Number of Nights</label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setNights(Math.max(pricing.min_nights, nights - 1))}
                  disabled={nights <= pricing.min_nights}
                  className="h-8 w-8"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <div className="flex-1 text-center">
                  <span className="text-lg font-semibold">{nights}</span>
                  <p className="text-xs text-gray-500">{pricing.min_nights}-{pricing.max_nights} nights</p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setNights(Math.min(pricing.max_nights, nights + 1))}
                  disabled={nights >= pricing.max_nights}
                  className="h-8 w-8"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Collapsible Sections */}
      <Accordion type="multiple" className="w-full">
        {/* Room Selection */}
        {rooms.length > 0 && (
          <AccordionItem value="room-selection">
            <AccordionTrigger className="text-lg font-semibold">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-shell-primary" />
                Room Selection
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="pt-6">
                  <div className="grid gap-3">
                    <div className="border rounded-lg p-3 bg-gray-50">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="room"
                          value=""
                          checked={selectedRoom === ""}
                          onChange={() => {
                            setSelectedRoom("");
                            setExtraPersons(0);
                          }}
                          className="text-shell-primary"
                        />
                        <div className="flex-1">
                          <div className="font-medium">Standard Room</div>
                          <div className="text-sm text-gray-600">Included in base package</div>
                          <div className="text-sm font-medium text-shell-primary">No additional cost</div>
                        </div>
                      </label>
                    </div>
                    
                    {rooms.map((room) => (
                      <div key={room.id} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="room"
                            value={room.id}
                            checked={selectedRoom === room.id}
                            onChange={() => {
                              setSelectedRoom(room.id);
                              setExtraPersons(0);
                            }}
                            className="text-shell-primary"
                          />
                          <div className="flex-1">
                            <div className="font-medium">{room.room_name}</div>
                            <div className="text-sm text-gray-600">
                              {room.room_type} • Capacity: {room.capacity} persons
                            </div>
                            <div className="text-sm font-medium text-shell-primary">
                              +${room.base_price} upgrade
                            </div>
                            {room.amenities && room.amenities.length > 0 && (
                              <div className="mt-1">
                                <div className="flex flex-wrap gap-1">
                                  {room.amenities.slice(0, 3).map((amenity, index) => (
                                    <span 
                                      key={index}
                                      className="px-2 py-0.5 bg-shell-primary/10 text-shell-primary rounded text-xs"
                                    >
                                      {amenity}
                                    </span>
                                  ))}
                                  {room.amenities.length > 3 && (
                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                      +{room.amenities.length - 3} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>

                  {/* Extra Persons for Selected Room */}
                  {selectedRoomData && selectedRoomData.max_extra_persons > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <label className="block text-sm font-medium mb-2">
                        Extra Persons (Max: {selectedRoomData.max_extra_persons})
                      </label>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setExtraPersons(Math.max(0, extraPersons - 1))}
                          disabled={extraPersons <= 0}
                          className="h-8 w-8"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <div className="flex-1 text-center">
                          <span className="text-lg font-semibold">{extraPersons}</span>
                          <p className="text-xs text-gray-500">
                            ${selectedRoomData.price_per_extra_person} per person
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setExtraPersons(Math.min(selectedRoomData.max_extra_persons, extraPersons + 1))}
                          disabled={extraPersons >= selectedRoomData.max_extra_persons}
                          className="h-8 w-8"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Activities & Services */}
        {addons.length > 0 && (
          <AccordionItem value="activities-services">
            <AccordionTrigger className="text-lg font-semibold">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-shell-primary" />
                Activities & Services
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {addons.map((addon) => (
                      <div key={addon.id} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <Checkbox
                            checked={selectedAddons.includes(addon.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedAddons([...selectedAddons, addon.id]);
                              } else {
                                setSelectedAddons(selectedAddons.filter(id => id !== addon.id));
                              }
                            }}
                            className="mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="font-medium">{addon.name}</div>
                            {addon.description && (
                              <div className="text-sm text-gray-600 mb-1">{addon.description}</div>
                            )}
                            <div className="text-sm font-medium text-shell-primary">
                              ${addon.price}
                              {addon.is_per_person && " per person"}
                              {addon.is_per_night && " per night"}
                              {addon.is_per_person && addon.is_per_night && ` (${addon.price * persons * nights} total)`}
                              {addon.is_per_person && !addon.is_per_night && ` (${addon.price * persons} total)`}
                              {!addon.is_per_person && addon.is_per_night && ` (${addon.price * nights} total)`}
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      {/* Price Summary */}
      <Card className="border-shell-primary/20 bg-shell-primary/5">
        <CardHeader>
          <CardTitle className="text-lg text-shell-dark">Price Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Base Package ({persons} persons × {nights} nights):</span>
              <span className="font-medium">
                ${(pricing.base_price + (pricing.per_person_price * persons) + (pricing.per_night_price * nights)).toFixed(2)}
              </span>
            </div>
            
            {selectedRoomData && (
              <div className="flex justify-between">
                <span>Room Upgrade ({selectedRoomData.room_name}):</span>
                <span className="font-medium">+${selectedRoomData.base_price.toFixed(2)}</span>
              </div>
            )}
            
            {selectedRoomData && extraPersons > 0 && (
              <div className="flex justify-between">
                <span>Extra Persons ({extraPersons}):</span>
                <span className="font-medium">
                  +${(selectedRoomData.price_per_extra_person * extraPersons).toFixed(2)}
                </span>
              </div>
            )}
            
            {selectedAddons.length > 0 && (
              <div className="space-y-1">
                {addons.filter(a => selectedAddons.includes(a.id)).map(addon => {
                  let cost = addon.price;
                  if (addon.is_per_person) cost *= persons;
                  if (addon.is_per_night) cost *= nights;
                  return (
                    <div key={addon.id} className="flex justify-between">
                      <span>{addon.name}:</span>
                      <span className="font-medium">+${cost.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <Separator className="my-3" />
          
          <div className="flex justify-between items-center text-xl font-bold text-shell-primary">
            <span>Total Price:</span>
            <span>${totalPrice.toFixed(2)}</span>
          </div>
          
          <p className="text-sm text-gray-600 text-center mt-2">
            Final price may vary based on availability and seasonal rates
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PackagePriceCalculator;
